/**
 * Feature Flags — Centralised runtime feature toggles
 *
 * All flags read from Vite env vars (VITE_*) which are string | undefined.
 * Boolean flags default to the value shown; override in .env.local.
 *
 * @module lib/featureFlags
 */

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function envBool(key: string, defaultValue: boolean): boolean {
  const raw = (import.meta as unknown as Record<string, Record<string, string>>).env?.[key];
  if (raw === undefined || raw === '') return defaultValue;
  return raw === 'true' || raw === '1';
}

// ─────────────────────────────────────────────────────────────────────────────
// eSignature Flags
// ─────────────────────────────────────────────────────────────────────────────

/** DocuSign eSignature integration (default: true in prod, true in demo for mock) */
export const ENABLE_DOCUSIGN = envBool('VITE_DOCUSIGN_ENABLED', true);

/** Browser-local canvas signing is a demo-only simulation, never a connected authority path. */
export const ENABLE_LOCAL_SIGNATURE = envBool('VITE_LOCAL_SIGNATURE_ENABLED', IS_DEMO_MODE);

// ─────────────────────────────────────────────────────────────────────────────
// Client Reports Flag (pre-existing)
// ─────────────────────────────────────────────────────────────────────────────

export const ENABLE_CLIENT_REPORTS = envBool('VITE_ENABLE_CLIENT_REPORTS', true);

// ─────────────────────────────────────────────────────────────────────────────
// Simplified Product Surface
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Enables the migration-first AX1 product surface.
 *
 * This flag changes navigation exposure only. It intentionally does not remove
 * routes, services, database tables, or historical records while the simplified
 * Project → Milestone → Capital Gate journey is being introduced.
 */
export const ENABLE_SIMPLIFIED_PRODUCT_SURFACE = envBool(
  'VITE_SIMPLIFIED_PRODUCT_SURFACE_ENABLED',
  true,
);

/** Internal AI-quality diagnostics are not part of the customer product surface. */
export const SHOW_AI_QUALITY = envBool(
  'VITE_SHOW_AI_QUALITY',
  !ENABLE_SIMPLIFIED_PRODUCT_SURFACE,
);

// ─────────────────────────────────────────────────────────────────────────────
// Convenience
// ─────────────────────────────────────────────────────────────────────────────

/** Returns all current flag values — useful for diagnostics / telemetry */
export function getAllFlags(): Record<string, boolean> {
  return {
    ENABLE_DOCUSIGN,
    ENABLE_LOCAL_SIGNATURE,
    ENABLE_CLIENT_REPORTS,
    ENABLE_SIMPLIFIED_PRODUCT_SURFACE,
    SHOW_AI_QUALITY,
  };
}
import { IS_DEMO_MODE } from '../config/env';
